import React, { useReducer, useEffect, useState } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  ButtonDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Row,
  Col,
  Label,
} from "reactstrap";
import LaddaButton, { EXPAND_RIGHT } from "react-ladda";
import PropTypes from "prop-types";
import CircularProgress from "../CircularProgress/CircularProgress";

import { initializeState, reducer, TICKET_REFERENCES, PROCESS_DATA, RESET, getProcessId } from "./reducer";
import style from "./styles.module.scss";
import { processes } from "../../utils/Helper";
import { NoPermissionWindow } from "../../utils/NoPermissionWindow";
import Graph, { buildGraphData } from "./Graph/Graph";
import CrossRefForms from "./Forms";
import { DateField } from "../../utils";
import { parseDate } from "../../utils/dateUtils";
import { XS } from "react-ladda/dist/constants";

const trimAll = (text) => text.replace(/"\s+|\s+"/g, '"');

const getTitle = (process, info, id, subId) => {
  if (process === processes.JOURNAL_ENTRY) {
    return `JournalEntry ${info.loading ? "" : `#${info.JournalEntryCorrelative} `}`;
  } else if (process === processes.ORDER && info.ExternalReference != null && info.ExternalReference != "") {
    return `Order #${id}${
      parseInt(subId, 10) > 0 ? `.${subId}` : ""
    }  - Ext. Ref: #${info.ExternalReference.slice(0, -3)}`;
  }
  return `${process} #${id}${parseInt(subId, 10) > 0 ? `.${subId}` : ""} `;
};

const getTicketTitle = ({ number, process, processId, subProcessId, processDescription = "" }) => {
  let res = "Ticket";
  if (!number) return res;
  res += ` #${number}`;
  if (!process || !processId) return res;
  res += ` (${process} #${processId}`;
  if (parseInt(subProcessId, 10) > 0) {
    res += `.${subProcessId}`;
  }
  if (processDescription !== "") {
    res += ` - ${processDescription}`;
  }
  res += ")";
  return res;
};

/** check if last tree data can be reused for this cross reference */
function canReuseLastTree(process, id, lastCrossRefTree) {
  if (lastCrossRefTree) {
    if (lastCrossRefTree instanceof Array) {
      return lastCrossRefTree.some((node) => {
        if (!node.data) {
          return false;
        }

        if (process == "CrossReference") {
          return node.id == id;
        }

        return node.data.processType == process && node.data.processId == id;
      });
    }
  }

  return false;
}

/** Store last calcualted tree, can be reused across item with same tree */
let lastCrossRefTree = null;

const CrossReference = ({ services, id, process, userType, platformRole, Link, opsViewAccountName }) => {
  const [state, dispatch] = useReducer(reducer, undefined, initializeState);
  const [openChilds, setOpenChilds] = useState(false);
  const [openActions, setOpenActions] = useState(false);
  const [treeData, setTreeData] = useState({ loadingId: null, data: null });
  const [errorPage, setErrorPage] = useState(false);
  const [revertC, setRevert] = useState(false);
  const [date, setDate] = useState(new Date());
  const [rollbacking, setRollbacking] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [downloadTicketLoading, setDownloadTicketLoading] = useState(false);
  const {
    references,
    ticketNumber,
    ticketMainProcess,
    ticketMainProcessId,
    ticketMainSubProcessId,
    isRolledback,
    isHidden,
    childs,
    parent,
    actions,
  } = state;

  const canRollback = actions ? actions.canRollback : false;
  const canChangeVisibility = actions ? actions.canChangeVisibility : false;

  const reset = (num) => {
    dispatch({
      type: RESET,
      payload: {
        ticketNumber: num,
      },
    });
  };

  const rollback = (crossReferenceId) => {
    setRollbacking(true);
    services.rollbackCrossReference({ crossReferenceId, confirmRollback: true }).then((res) => {
      // TODO
      if (res.result.Code === 200) {
        alert("Correctly rollbacked");
        setRollbacking(false);
      } else {
        alert(`Failed! ${res.result.Description}`);
        setRollbacking(false);
      }
    });
  };

  const revert = (crossReferenceId) => {
    setReverting(true);
    services.revertCrossReference({ crossReferenceId, date, confirmRollback: true }).then((res) => {
      // TODO
      if (res.result.Code === 200) {
        setReverting(false);
        alert("Correctly reverted");
      } else {
        setReverting(false);
        alert(`Failed! ${res.result.Description}`);
      }
    });
  };
  const downloadTicket = ({ crossreferenceid, caller }) => {
    setDownloadTicketLoading(() => true);
    services
      .fetchTicket({
        crossreferenceid,
        caller,
      })
      .then((response) => {
        if (response.code !== 0) {
          alert(response.message);
        } else {
          window.open(response.url);
        }
      })
      .finally(() => setDownloadTicketLoading(() => false));
  };

  const changeVisibility = (crossReferenceId, hide) => {
    services.setVisibilityOfRollbackedJournalEntry({ crossReferenceId, hide }).then((res) => {
      // TODO
      if (res.result.Code === 200) {
        if (hide) {
          alert("Correctly hidden");
        } else {
          alert("Correctly unhidden");
        }
      } else {
        alert(`Failed! ${res.result.Description}`);
      }
    });
  };

  const onChangeDate = (name, value) => {
    const auxdate = new Date(value).toLocaleDateString("en");
    setDate(value);
  };

  useEffect(() => {
    services.ticketReference({ processes: process, id, includeChilds: false }).then((res) => {
      const {
        processes: newProcesses,
        id: CrossReferenceId,
        mainProcess,
        mainProcessId,
        mainSubProcessId,
        isRolledback: newIsRolledback,
        isHidden: newIsHidden,
        childs: newChilds,
        parent: newParent,
        actions: newActions,
      } = res.crossreference;
      const action = {
        type: TICKET_REFERENCES,
        payload: {
          references: newProcesses || [],
          ticketNumber: CrossReferenceId,
          ticketMainProcess: mainProcess,
          ticketMainProcessId: mainProcessId,
          ticketMainSubProcessId: mainSubProcessId,
          isHidden: newIsHidden,
          isRolledback: newIsRolledback,
          childs: newChilds,
          parent: newParent,
          actions: newActions,
        },
      };
      dispatch(action);
    });

    /** reuse tree if last tree contains current node id */
    new Promise((resolveTree) => {
      if (canReuseLastTree(process, id, lastCrossRefTree)) {
        resolveTree(lastCrossRefTree);
      } else {
        // tree must be recalculated
        services.crossTree({ processes: process, id }).then((res) => {
          if (res) {
            lastCrossRefTree = res.nodes;
            resolveTree(res.nodes);
          }
        });
      }
    }).then((tree) => {
      const data = buildGraphData(tree);
      if (data) {
        setTreeData({ data });
      } else {
        setTreeData({ data: null });
      }
    });
  }, [process, id]);

  useEffect(() => {
    if (references) {
      references.forEach((ref) => {
        const { process: refProcess, ids } = ref;
        ids.forEach(({ id: refId, subId }) => {
          services.crossRelatedInfo({ process: refProcess, id: refId, subId }).then((res) => {
            const properties = JSON.parse(res.propertiesAsJson);
            if (properties.Error) {
              setErrorPage(true);
            } else {
              let accessProcess = refProcess;
              if (
                refProcess === processes.WITHDRAW ||
                refProcess === processes.BULK_WITHDRAW ||
                refProcess === processes.DEPOSIT ||
                refProcess === processes.BULK_DEPOSIT ||
                refProcess === processes.TRANSFER ||
                refProcess === processes.WITHDRAW_ACCOUNT ||
                refProcess === processes.DEPOSIT_ACCOUNT
              ) {
                accessProcess = "IO";
              }
              const action = {
                type: PROCESS_DATA,
                payload: {
                  process: refProcess,
                  id: refId,
                  subId,
                  [refProcess]: JSON.parse(trimAll(properties[accessProcess] || "{}")),
                },
              };
              if (refProcess === processes.JOURNAL_ENTRY) {
                action.payload[processes.JOURNAL_ENTRY][processes.CASH_ACTIVITY] = JSON.parse(
                  trimAll(properties[processes.CASH_ACTIVITY])
                );
                action.payload[processes.JOURNAL_ENTRY][processes.PRODUCT_ACTIVITY] = JSON.parse(
                  trimAll(properties[processes.PRODUCT_ACTIVITY])
                );
              }
              dispatch(action);
            }
          });
        });
      });
    }
  }, [references]);

  const crossReferenceGraphOnClick = (e, data) => {
    if (ticketNumber != data.id) {
      setTreeData({ ...treeData, loadingId: data.id });
    }
  };

  if (errorPage) {
    return NoPermissionWindow();
  }

  return (
    <React.Fragment>
      <h3 className="my-3">
        {getTicketTitle({
          number: ticketNumber,
          process: ticketMainProcess,
          processId: ticketMainProcessId,
          subProcessId: ticketMainSubProcessId,
        })}
      </h3>
      {!references && <CircularProgress />}
      <Row className="mb-3">
        {parent && parseInt(parent.id, 10) > 0 && (
          <Col>
            <Link to={`/crossReference/CrossReference/${parent.id}`}>
              <Button onClick={() => reset(parent.id)} color="light">
                {`Go to Parent ${getTicketTitle({
                  number: parent.id,
                  process: parent.mainProcess,
                  processId: parent.mainProcessId,
                  subProcessId: parent.subProcessId,
                })}`}
              </Button>
            </Link>
          </Col>
        )}
        {childs && childs.length > 0 && (
          <Col xs={6} md={4} lg={3}>
            <ButtonDropdown isOpen={openChilds} toggle={() => setOpenChilds((o) => !o)}>
              <DropdownToggle className="btn-light" caret>
                Child Tickets
              </DropdownToggle>
              <DropdownMenu>
                <DropdownItem header>Child Tickets</DropdownItem>
                <DropdownItem divider />
                {childs.map((child) => (
                  <Link to={`/crossReference/CrossReference/${child.id}`} key={child.id}>
                    <DropdownItem onClick={() => reset(child.id)}>
                      {`Go to Child ${getTicketTitle({
                        number: child.id,
                        process: child.mainProcess,
                        processId: child.mainProcessId,
                        subProcessId: child.mainSubProcessId,
                        processDescription: child.mainProcessDescription,
                      })}`}
                    </DropdownItem>
                  </Link>
                ))}
              </DropdownMenu>
            </ButtonDropdown>
          </Col>
        )}
        {(canRollback || canChangeVisibility) && !rollbacking && !reverting && (
          <Col xs={1} md={1} lg={1}>
            <ButtonDropdown isOpen={openActions} toggle={() => setOpenActions((o) => !o)}>
              <DropdownToggle className="btn-light" caret>
                Actions
              </DropdownToggle>
              <DropdownMenu>
                {canRollback && !isRolledback && (
                  <DropdownItem onClick={() => rollback(ticketNumber)}>Rollback</DropdownItem>
                )}
                {canRollback && !isRolledback && (
                  <DropdownItem onClick={() => setRevert(!revertC)}>Revert</DropdownItem>
                )}
                {canChangeVisibility && isRolledback && (
                  <DropdownItem onClick={() => changeVisibility(ticketNumber, !isHidden)}>
                    {isHidden ? "Unhide activity" : "Hide activity"}
                  </DropdownItem>
                )}
              </DropdownMenu>
            </ButtonDropdown>
          </Col>
        )}
      </Row>
      <Row>
        {revertC && <Col xs={6} md={4} lg={3} />}
        {revertC && (
          <Col xs={6} md={4} lg={3}>
            <DateField
              label=""
              name="date"
              onChange={(name, value) => onChangeDate(name, value)}
              value={date}
              disabled={false}
            />
          </Col>
        )}
        {revertC && (
          <Col xs={6} md={4} lg={3} style={{ paddingTop: 8 }}>
            <LaddaButton
              className="btn btn-ladda btn-primary pl-3 pr-3"
              loading={reverting}
              onClick={() => revert(ticketNumber)}
              data-style={EXPAND_RIGHT}
              data-size={XS}
            >
              <i className="fa fa-repeat" aria-hidden="true"></i>
            </LaddaButton>
          </Col>
        )}
      </Row>
      {treeData.data && (
        <Row>
          <Col lg="12">
            <Card className={`${style.card} mb-3`}>
              <CardBody>
                <Graph
                  data={treeData.data}
                  horizontal={true}
                  collapsable={true}
                  expandAll={true}
                  selected={ticketNumber}
                  loadingId={treeData.loadingId}
                  onClick={crossReferenceGraphOnClick}
                  renderContent={(e) => (
                    <Link
                      to={`/crossReference/CrossReference/${e.id}`}
                      className={style.treeItem}
                      disabled={e.id === ticketNumber}
                      key={`crossreference-graph-link-${e.id}`}
                    >
                      {e.label}
                    </Link>
                  )}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
      {references &&
        references.map((r) => (
          <React.Fragment key={r.process}>
            {r.ids.map(({ id: processId, subId }, index) => (
              <Card key={`${r.process}-${processId}.${subId}`} className={`${style.card} mb-3`}>
                <CardHeader className="pt-2 pb-2">
                  {getTitle(r.process, state[r.process][processId], processId, subId)}
                </CardHeader>
                <CardBody className="px-3">
                  <div key={processId} className="mb-3">
                    {state[r.process][getProcessId(processId, subId)].loading && <CircularProgress sm />}
                    {!state[r.process][getProcessId(processId, subId)].loading && (
                      <CrossRefForms
                        userType={userType}
                        platformRole={platformRole}
                        process={r.process}
                        info={state[r.process][getProcessId(processId, subId)]}
                        services={services}
                        opsViewAccountName={opsViewAccountName}
                      />
                    )}
                    {r.ids.length > index + 1 && <hr />}
                  </div>
                </CardBody>
                <Col className="text-right">
                  {r.process === processes.TRADE && (
                    <React.Fragment>
                      {downloadTicketLoading ? (
                        <div className={style.downloadTicketLoading}>
                          <CircularProgress sm />
                        </div>
                      ) : (
                        <Button
                          color={downloadTicketLoading ? "" : "primary"}
                          onClick={() => downloadTicket({ crossreferenceid: ticketNumber, caller: "CR" })}
                          disabled={downloadTicketLoading}
                        >
                          <i className="fa fa-file-text" aria-hidden="true"></i>
                        </Button>
                      )}
                    </React.Fragment>
                  )}
                </Col>
              </Card>
            ))}
          </React.Fragment>
        ))}
      {references && references.length === 0 && <p>No Cross References</p>}
    </React.Fragment>
  );
};

CrossReference.propTypes = {
  services: PropTypes.object.isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  process: PropTypes.string.isRequired,
  userType: PropTypes.string.isRequired,
  Link: PropTypes.any.isRequired,
};

export default CrossReference;
